/** The 5/50 EMA panels: what the engine is doing, and what it decided.
 *
 * Self-gates on strategy type, so this renders nothing for bias, BB,
 * supertrend or theta.
 *
 * Today's numbers (VIX, ATR, the risk band, the ladder rungs) come from the
 * SAME card the dashboard shows, mounted here too rather than copied — the
 * operator reads them on the strategy page, and two versions of a stop
 * distance that could drift apart is the one thing this must not become.
 */

import { useState, type ReactElement } from "react";
import { StrategySizeCard } from "./StrategySizeCard";
import { useQuery } from "@tanstack/react-query";
import { EmaTrendStatusCard } from "./EmaTrendStatusCard";
import { EmaTrendParametersCard } from "@/components/Dashboard/EmaTrendParametersCard";
import { Trail } from "./DecisionTrail";
import { emaTrendApi } from "@/lib/emaTrendApi";
import type { Strategy } from "@/types/strategy";

const POLL_MS = 15_000;

/** Which part of the rule spoke. */
const CHANNEL: Record<string, string> = {
  structure: "Structure",
  entry: "Entry",
  order: "Limit",
  position: "Position",
};

function labelOf(channel: string): string {
  return CHANNEL[channel] ?? channel;
}

export function EmaTrendPanels({
  strategy,
}: Readonly<{ strategy: Strategy }>): ReactElement | null {
  if (strategy.strategy_type !== "ema_trend") return null;
  return (
    <>
      <EmaTrendParametersCard />
      {/* Since 2026-09-20 this engine sizes by premium cost and takes POI-1's
          expiry-day strike rule, so it needs the same two numbers. */}
      <StrategySizeCard strategy={strategy} />
      <EmaTrendStatusCard strategyId={strategy.id} />
      <EmaTrendDecisionTrailCard strategyId={strategy.id} />
    </>
  );
}

/** Closed by default — read when something needs explaining, not watched. */
function EmaTrendDecisionTrailCard({ strategyId }: Readonly<{ strategyId: string }>): ReactElement {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["ema-trend-decisions", strategyId],
    queryFn: () => emaTrendApi.decisions(strategyId),
    refetchInterval: POLL_MS,
    enabled: open,
  });
  const rows = q.data ?? [];
  return (
    <section className="card">
      <details
        open={open}
        onToggle={(e) => {
          setOpen(e.currentTarget.open);
        }}
      >
        <summary className="flex cursor-pointer select-none items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <i className="ti ti-list-search text-lg text-accent" />
            <h3 className="inline">Today&apos;s decisions</h3>
          </span>
          {rows.length > 0 && (
            <span className="text-xs text-ink-muted">{rows.length} recorded</span>
          )}
        </summary>
        <div className="mt-3">
          <Trail rows={rows} loading={q.isPending} labelOf={labelOf} />
        </div>
      </details>
    </section>
  );
}
